// Service-worker update decision logic (pure, unit-testable).
//
// A new build's service worker activates (skipWaiting + clientsClaim are set in
// vite.config.js), and vite-plugin-pwa's autoUpdate mode would otherwise call
// window.location.reload() the instant it activates — INCLUDING while a user is
// mid-round on the scorecard. Reloading during live scoring is a data/UX hazard
// (F-074). This helper decides whether a page reload is safe RIGHT NOW based on
// the surface the user is currently on.
//
// Wired in src/main.jsx via registerSW({ onNeedReload }). Kept pure (no window,
// no DOM) so it can be unit-tested in node — the caller passes the current
// pathname.

// Surfaces where a full-page reload would interrupt live scoring / an in-progress
// round. Matches the protected set used by ColdOpenToHome in src/App.jsx so the
// two "don't yank the user mid-round" guards stay in agreement.
export const PROTECTED_ROUND_PATHS = ['/scorecard', '/round-leaderboard']

// True when the given pathname is an active scoring / round surface where an
// update reload must NOT happen.
export function isActiveRoundPath(pathname) {
  const path = String(pathname || '')
  return PROTECTED_ROUND_PATHS.includes(path)
}

// Whether it is safe to apply a waiting service-worker update by reloading the
// page right now. Safe everywhere EXCEPT the active round surfaces. When it
// returns false, the caller defers the reload (re-checking on the next safe
// foreground, on the next in-app navigation to a safe path, and a cold app
// launch always picks up the new build regardless).
export function shouldReloadForUpdate(pathname) {
  return !isActiveRoundPath(pathname)
}

// Decide what to do when a lazy-loaded chunk (e.g. the GPS map) fails to load —
// which happens when a new build has been published and the old content-hashed
// chunk URL now 404s. On a SAFE surface we can reload immediately to fetch the
// current build's chunk. On a PROTECTED (active-round) surface we must NOT
// reload — we defer, keep the scorecard mounted, and surface a soft notice so
// live scoring is never interrupted. Returns 'reload' or 'defer'.
export function handlePreloadError(pathname) {
  return isActiveRoundPath(pathname) ? 'defer' : 'reload'
}

// Gate for applying a deferred/waiting update: only apply when there is
// something pending AND the current surface is safe to reload. Pure so the
// caller's applyPendingUpdateIfSafe() gating is unit-testable without a browser.
export function shouldApplyPendingUpdate(pending, pathname) {
  return !!pending && shouldReloadForUpdate(pathname)
}

// Whether the scorecard's per-hole-save AUTO-reopen of the GPS map should fire.
// The scorecard re-opens the map after each save ONLY when the user engaged GPS
// at least once this round (`gpsOpenedThisRound`). F-074: once the GPS-map lazy
// chunk has FAILED to load this round (a new build published mid-round purged
// the old content-hashed chunk), the auto-reopen is SUPPRESSED for the rest of
// the round — otherwise the local map ErrorBoundary would re-render the
// full-screen "map unavailable" overlay on EVERY subsequent hole save,
// interrupting live scoring hole after hole. The user can still MANUALLY reopen
// the map (a user gesture); only the automatic reopen is gated here. Pure so
// the suppression is unit-testable without a browser.
export function shouldAutoReopenMap({ gpsOpenedThisRound, mapLoadFailedThisRound } = {}) {
  return !!gpsOpenedThisRound && !mapLoadFailedThisRound
}

// F-074 FIX 2: the RESUME auto-open decision, extracted so ScorecardPage's
// resume-after-navigation path calls exactly this instead of an inline check.
// On a resume the "GPS was the last-viewed screen this round" signal plays the
// role of gpsOpenedThisRound, and the round's persisted failure latch plays the
// role of the load-failure gate. Delegating to shouldAutoReopenMap keeps ONE
// source of truth for "should the map auto-open" across the resume, per-hole-save,
// and mount-hydrate paths — remove the latch input from any of them and the
// decision flips. Pure so the resume decision is unit-testable without a browser.
export function resolveResumeAutoOpen({ gpsWasOpen, roundMapFailed } = {}) {
  return shouldAutoReopenMap({
    gpsOpenedThisRound: gpsWasOpen,
    mapLoadFailedThisRound: roundMapFailed,
  })
}

// F-poi-map-routing: decide WHICH in-round GPS map surface to render, decoupled
// from course provenance. Historically the scorecard chose the surface purely
// from course.source: source==='manual' → the pin-drop ManualHoleMap, anything
// else → the POI rangefinder HoleMap. But `source` is a deliberately-immutable
// provider-of-record enum, and the admin POI mapper stamps real per-hole
// coordinates into course_coordinates WITHOUT changing source. So a course
// created via the manual path and later hand-mapped by an admin has genuine
// POIs yet was stuck on the coordinate-less ManualHoleMap. The correct signal is
// "does this course actually have admin POI coordinates loaded?" — captured by
// `hasPoiCoords`. Returns 'poi' | 'manual', and the two are mutually exclusive
// by construction: a manual course renders 'manual' ONLY when it has no POIs;
// once POIs exist (or it was never a manual course) it renders 'poi'. Pure so
// the surface decision is unit-testable as a runtime value without a browser.
export function pickMapSurface({ source, hasPoiCoords } = {}) {
  if (source === 'manual' && !hasPoiCoords) return 'manual'
  return 'poi'
}

// F-074 FIX 1: shared prefix for every per-round map-failure latch key. Exported
// so ScorecardPage can sweep sibling latches (belt-and-suspenders round-start
// clear) without duplicating the literal.
export const MAP_FAILED_KEY_PREFIX = 'tf_map_failed_'

// F-074 FIX 1: sessionStorage key for the per-round map-failure latch. Keyed to a
// PER-ROUND-INSTANCE id (casual: standalone_round_id; event: event id + the
// active-round token so AM/PM and multi-day league rounds of the SAME event each
// get their own key — the shared event.id alone would latch every later round of
// the event OFF after one transient failure). The latch must stay latched across
// ScorecardPage REMOUNTS (the user navigates away and back — e.g. to /game or
// /shuffle — which resets every per-mount ref), so NO auto-reopen path
// (per-hole-save OR resume-after-nav) can re-throw the purged import and re-show
// the "map unavailable" overlay for the rest of THAT round. A NEW round instance
// has a new id → a new key that reads unset → starts clean, with no explicit
// migration. Returns null when there is no round id yet (nothing to persist /
// read). Pure so the keying is unit-testable.
export function mapFailedStorageKey(roundId) {
  if (roundId == null || roundId === '') return null
  return `${MAP_FAILED_KEY_PREFIX}${roundId}`
}

// F-074 FIX 1: interpret a persisted map-failure latch value. It is stored as the
// string '1' the moment the GPS-map chunk fails to load this round; anything else
// (absent → null, '0', etc.) means "not failed". Pure so the mount-time
// re-hydration of mapLoadFailedThisRound is unit-testable without a browser.
export function isRoundMapFailed(storedValue) {
  return storedValue === '1'
}
